import styled, { css } from 'styled-components';

import { Container, ContentContainer } from '@components/atoms';
import {
  TERMS_AND_CONDITIONS_INTRODUCTION,
  TERMS_AND_CONDITIONS_META,
  TERMS_AND_CONDITIONS_SECTIONS,
  TERMS_AND_CONDITIONS_TITLE,
} from '@shared/constants/termsAndConditions';
import { device } from '@styles/mediaQuery';

function TermsAndConditionsPageContent() {
  return (
    <PageContainer>
      <Content>
        <Header>
          <Eyebrow>GREEN LINE LOGISTICS, LLC</Eyebrow>
          <Title>{TERMS_AND_CONDITIONS_TITLE}</Title>
          <Meta>{TERMS_AND_CONDITIONS_META}</Meta>
        </Header>

        <Introduction>{TERMS_AND_CONDITIONS_INTRODUCTION}</Introduction>

        {TERMS_AND_CONDITIONS_SECTIONS.map(({ number, title, content }) => (
          <Section key={number}>
            <SectionTitle>
              <Number>{number}.</Number> {title}
            </SectionTitle>
            <Paragraph>{content}</Paragraph>
          </Section>
        ))}

        <Section>
          <SectionTitle>
            <Number>17.</Number> Contact
          </SectionTitle>
          <ContactList>
            <li>Green Line Logistics, LLC</li>
            <li>
              Email:{' '}
              <ContactLink href="mailto:legal@greenlinelog.com">
                legal@greenlinelog.com
              </ContactLink>
            </li>
            <li>
              Website:{' '}
              <ContactLink href="https://greenlinelog.com/">
                https://greenlinelog.com/
              </ContactLink>
            </li>
          </ContactList>
        </Section>
      </Content>
    </PageContainer>
  );
}

const PageContainer = styled(Container)`
  overflow: visible;
  padding-top: 116px;
  padding-bottom: 64px;

  ${device.tablet(css`
    padding-top: 148px;
    padding-bottom: 88px;
  `)}
`;

const Content = styled(ContentContainer)`
  max-width: 1040px;
`;

const Header = styled.header`
  margin-bottom: 28px;

  ${device.tablet(css`
    margin-bottom: 34px;
  `)}
`;

const Eyebrow = styled.p`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.colors.forestGreen};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 3px;
  text-transform: uppercase;

  ${device.tablet(css`
    font-size: 16px;
  `)}
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 38px;
  font-weight: 700;
  line-height: 44px;

  ${device.tablet(css`
    font-size: 52px;
    line-height: 60px;
  `)}
`;

const Meta = styled.p`
  margin: 14px 0 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  line-height: 24px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 28px;
  `)}
`;

const Introduction = styled.p`
  margin: 0 0 16px;
  padding: 22px 20px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-left: 4px solid ${({ theme }) => theme.colors.forestGreen};
  font-size: 17px;
  line-height: 27px;

  ${device.tablet(css`
    padding: 28px 30px;
    font-size: 19px;
    line-height: 30px;
  `)}
`;

const Section = styled.section`
  padding: 28px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  ${device.tablet(css`
    padding: 36px 0;
  `)}

  &:last-of-type {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;

  ${device.tablet(css`
    font-size: 30px;
    line-height: 38px;
  `)}
`;

const Number = styled.span`
  margin-right: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Paragraph = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 26px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 30px;
  `)}
`;

const ContactList = styled.ul`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 28px;

  ${device.tablet(css`
    font-size: 18px;
    line-height: 31px;
  `)}
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.forestGreen};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default TermsAndConditionsPageContent;
